/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import Rollbar from 'rollbar/replay';
import * as Sentry from '@sentry/browser';
import appSettings from '../config/settings';
import { StatusCode } from './enums';
import { isNetworkError } from '../util/errors';

const ignoreErrorsMap = {
  ['ResizeObserver loop limit exceeded']: true, // Some exotic browsers seems to emit these.
  ['Error reading']: true, // Ignore file reader errors (ImageFromFile)
  ['AxiosError: Network Error']: true,
  ['Network Error']: true,
  ['Load failed']: true,
  ['Failed to fetch']: true,
  ['AxiosError: timeout exceeded']: true,
  ['Request aborted']: true,
  ['AxiosError: Request aborted']: true,
  ['TypeError: Load failed']: true,
  ['TypeError: Failed to fetch']: true,
  ['TypeError: Network Error']: true,
  ['Unexpected EOF']: true,
  ['JSON Parse error: Unexpected EOF']: true,
  ['ChunkLoadError']: true,
  ['Loading chunk']: true,
  ['Loading CSS chunk']: true,
  ["Failed to execute 'json' on 'Response': body stream already read"]: true, // Infrastructure noise/race condition from multiple concurrent syncs.
  ['Minified React error #418']: true, // Hydration mismatch (SSR vs client diff) — root cause is in Topbar, suppressed until investigated.
  ['Minified React error #423']: true, // React's recovery from #418 — suppress alongside it.
  ['Invalid call to runtime.sendMessage(). Tab not found.']: true, // Browser extension noise (e.g. Safari on iOS)
  ['window.webkit.messageHandlers']: true, // Facebook in-app browser (FBIOS) injects scripts that call unregistered iOS WebKit native bridge handlers
  ['Could not find identifiable element']: true, // Safari Auto-fill / 1Password extension noise when interacting with forms
  ['Blocked a frame with origin']: true, // Cross-origin iframe SecurityError from Rollbar Replay SDK's onIframeLoad — benign, replay just skips that iframe
};
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_DEPTH = 4;
const MAX_OBJECT_KEYS = 25;
const MAX_STRING_LENGTH = 500;
const MAX_CLASSLIST_ITEMS = 10;
const MAX_RECENT_CSP_VIOLATIONS = 5;
const RECENT_CSP_VIOLATION_WINDOW_MS = 2 * 60 * 1000;
const RECENT_BROWSER_ISSUE_WINDOW_MS = 30 * 1000;
const ROLLBAR_ENABLED = Boolean(appSettings.rollbarAccessToken) && appSettings.env !== 'test';
const STACK_OVERFLOW_MESSAGE_RE = /maximum call stack size exceeded/i;
const TRANSLATION_RESOURCE_RE = /(translate(_http)?|translate\.google|gstatic.*translate|\/_\/translate_http\/|goog-te|VIpgJd)/i;
const UNKNOWN_FRAME_FILENAME = '(unknown)';
const WEBKIT_MASKED_FRAME_FILENAME = 'webkit-masked-url://hidden/';
const SENSITIVE_FIELD_MATCHERS = [
  /pass(word)?/i,
  /secret/i,
  /token/i,
  /cookie/i,
  /authorization/i,
  /bearer/i,
  /api[-_]?key/i,
  /card/i,
  /cvc/i,
  /cvv/i,
  /bank[-_]?account/i,
  /external[-_]?accounts?/i,
  /account[-_]?number/i,
  /routing[-_]?number/i,
  /bank[-_]?name/i,
  /iban/i,
  /ssn/i,
  /captcha/i,
  /email/i,
  /phone/i,
  /address/i,
  /postal/i,
  /zip/i,
  /firstName/i,
  /lastName/i,
  /fullName/i,
  /username/i,
];
const rollbarScrubFields = [
  'access_token',
  'accessToken',
  'api_key',
  'apiKey',
  'authorization',
  'bearer',
  'captcha',
  'card',
  'cardNumber',
  'cookie',
  'cookies',
  'creditCardNumber',
  'cvv',
  'cvc',
  'bank_account',
  'bankAccount',
  'external_account',
  'external_accounts',
  'externalAccount',
  'externalAccounts',
  'account_number',
  'accountNumber',
  'routing_number',
  'routingNumber',
  'bank_name',
  'bankName',
  'email',
  'firstName',
  'fullName',
  'lastName',
  'password',
  'passwordConfirmation',
  'phone',
  'postalCode',
  'secret',
  'set-cookie',
  'token',
  'username',
  'zip',
];

let rollbar = null;
let rollbarSetupAttempted = false;
let currentUserId = null;
let browserDiagnosticsSetupAttempted = false;
let recentSecurityPolicyViolations = [];
let recentBrowserIssueFingerprints = new Map();

const pickSelectedErrors = (ignored, entry) => {
  const [key, value] = entry;
  return value === true ? [...ignored, key] : ignored;
};

const ignoredMessages = Object.entries(ignoreErrorsMap).reduce(pickSelectedErrors, []);
const isSensitiveKey = key => SENSITIVE_FIELD_MATCHERS.some(pattern => pattern.test(String(key)));

const truncateString = value =>
  typeof value === 'string' && value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
    : value;

const summarizeError = (error, depth, seen) => {
  const summary = {
    name: error.name,
    message: truncateString(error.message),
  };

  if (error.stack) {
    summary.stack = truncateString(error.stack);
  }
  if (error.status != null) {
    summary.status = error.status;
  }
  if (error.statusText) {
    summary.statusText = truncateString(error.statusText);
  }
  if (error.code) {
    summary.code = error.code;
  }
  if (error.data != null) {
    summary.data = sanitizeValue(error.data, depth + 1, seen);
  }
  if (error.cause) {
    summary.cause = sanitizeValue(error.cause, depth + 1, seen);
  }

  return summary;
};

const sanitizeValue = (value, depth = 0, seen = new WeakSet()) => {
  if (value == null) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return summarizeError(value, depth, seen);
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'function') {
    return `[Function ${value.name || 'anonymous'}]`;
  }

  if (typeof value === 'symbol') {
    return String(value);
  }

  if (depth >= MAX_OBJECT_DEPTH) {
    return Array.isArray(value) ? '[Array]' : '[Object]';
  }

  if (Array.isArray(value)) {
    const items = value.slice(0, MAX_ARRAY_ITEMS).map(item => sanitizeValue(item, depth + 1, seen));
    return value.length > MAX_ARRAY_ITEMS
      ? [...items, `[${value.length - MAX_ARRAY_ITEMS} more items]`]
      : items;
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);
    const keys = Object.keys(value);
    const sanitized = keys.slice(0, MAX_OBJECT_KEYS).reduce((collected, key) => {
      collected[key] = isSensitiveKey(key)
        ? '[REDACTED]'
        : sanitizeValue(value[key], depth + 1, seen);
      return collected;
    }, {});

    if (keys.length > MAX_OBJECT_KEYS) {
      sanitized.__truncatedKeys = keys.length - MAX_OBJECT_KEYS;
    }

    return sanitized;
  }

  return String(value);
};

const getBrowserContext = () => {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }

  const { pathname, search, hash } = window.location;
  return {
    route: pathname,
    page: `${pathname}${search}${hash}`,
  };
};

const getNavigatorConnectionInfo = () => {
  if (typeof navigator === 'undefined' || !navigator.connection) {
    return null;
  }

  const { effectiveType, downlink, rtt, saveData, type } = navigator.connection;
  return {
    effectiveType: effectiveType || null,
    downlink: downlink ?? null,
    rtt: rtt ?? null,
    saveData: typeof saveData === 'boolean' ? saveData : null,
    type: type || null,
  };
};

const summarizeClassList = element =>
  element?.classList ? Array.from(element.classList).slice(0, MAX_CLASSLIST_ITEMS) : [];

const summarizeActiveElement = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  const activeElement = document.activeElement;
  if (!activeElement || [document.body, document.documentElement].includes(activeElement)) {
    return null;
  }

  return {
    tagName: activeElement.tagName || null,
    id: activeElement.id || null,
    name: activeElement.getAttribute?.('name') || null,
    type: activeElement.getAttribute?.('type') || null,
    role: activeElement.getAttribute?.('role') || null,
    title: truncateString(activeElement.getAttribute?.('title') || ''),
    ariaLabel: truncateString(activeElement.getAttribute?.('aria-label') || ''),
    classList: summarizeClassList(activeElement),
  };
};

const trimRecentSecurityPolicyViolations = () => {
  const cutoff = Date.now() - RECENT_CSP_VIOLATION_WINDOW_MS;
  recentSecurityPolicyViolations = recentSecurityPolicyViolations.filter(
    violation => violation.timestampMs >= cutoff
  );
  return recentSecurityPolicyViolations;
};

const isTranslationRelatedSecurityPolicyViolation = violation => {
  const candidate = [
    violation?.blockedURI,
    violation?.sourceFile,
    violation?.originalPolicy,
    violation?.violatedDirective,
    violation?.effectiveDirective,
  ]
    .filter(Boolean)
    .join(' ');

  return TRANSLATION_RESOURCE_RE.test(candidate);
};

const recordSecurityPolicyViolation = event => {
  const nextEntry = {
    timestampMs: Date.now(),
    blockedURI: truncateString(event?.blockedURI || ''),
    violatedDirective: truncateString(event?.violatedDirective || ''),
    effectiveDirective: truncateString(event?.effectiveDirective || ''),
    sourceFile: truncateString(event?.sourceFile || ''),
    disposition: truncateString(event?.disposition || ''),
    originalPolicy: truncateString(event?.originalPolicy || ''),
  };

  recentSecurityPolicyViolations = [...trimRecentSecurityPolicyViolations(), nextEntry].slice(
    -MAX_RECENT_CSP_VIOLATIONS
  );
};

const getTranslationDiagnostics = () => {
  if (typeof document === 'undefined') {
    return {
      likelyActive: false,
      signals: [],
      recentTranslateCspViolations: [],
    };
  }

  const translationSignals = [];
  const hasSignal = (label, selector) => {
    try {
      if (document.querySelector(selector)) {
        translationSignals.push(label);
      }
    } catch (e) {
      // Ignore invalid selectors
    }
  };

  hasSignal(
    'translate-stylesheet',
    'link[href*="translate_http"], style[data-href*="translate_http"]'
  );
  hasSignal(
    'translate-ui',
    'iframe[src*="translate"], iframe[class*="goog-te"], [class*="goog-te"], [id*="goog-gt-"], [class*="VIpgJd"]'
  );

  const htmlClassList = summarizeClassList(document.documentElement);
  const bodyClassList = summarizeClassList(document.body);

  if (htmlClassList.some(cls => /^translated-(ltr|rtl)$/i.test(cls))) {
    translationSignals.push('translated-html-class');
  }
  if (bodyClassList.some(cls => /^translated-(ltr|rtl)$/i.test(cls))) {
    translationSignals.push('translated-body-class');
  }

  const recentTranslateCspViolations = trimRecentSecurityPolicyViolations()
    .filter(isTranslationRelatedSecurityPolicyViolation)
    .map(violation => ({
      blockedURI: violation.blockedURI || null,
      violatedDirective: violation.violatedDirective || null,
      effectiveDirective: violation.effectiveDirective || null,
      sourceFile: violation.sourceFile || null,
    }));

  const signals = Array.from(new Set(translationSignals));

  return {
    likelyActive: signals.length > 0 || recentTranslateCspViolations.length > 0,
    signals,
    htmlLang: document.documentElement?.lang || null,
    htmlClassList,
    bodyClassList,
    recentTranslateCspViolations,
  };
};

export const getClientMonitoringDiagnostics = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const { route, page } = getBrowserContext();
  const visualViewport = window.visualViewport;
  const viewport = {
    innerWidth: window.innerWidth ?? null,
    innerHeight: window.innerHeight ?? null,
    outerWidth: window.outerWidth ?? null,
    outerHeight: window.outerHeight ?? null,
    devicePixelRatio: window.devicePixelRatio ?? null,
    visualViewportWidth: visualViewport?.width ?? null,
    visualViewportHeight: visualViewport?.height ?? null,
    visualViewportScale: visualViewport?.scale ?? null,
  };

  return {
    route: route || null,
    page: page || null,
    visibilityState: document?.visibilityState || null,
    readyState: document?.readyState || null,
    referrer: document?.referrer || null,
    online: typeof navigator?.onLine === 'boolean' ? navigator.onLine : null,
    language: navigator?.language || null,
    languages: Array.isArray(navigator?.languages) ? navigator.languages.slice(0, 5) : null,
    userAgent: truncateString(navigator?.userAgent || ''),
    viewport,
    screen: window.screen
      ? {
          width: window.screen.width ?? null,
          height: window.screen.height ?? null,
          availWidth: window.screen.availWidth ?? null,
          availHeight: window.screen.availHeight ?? null,
        }
      : null,
    connection: getNavigatorConnectionInfo(),
    activeElement: summarizeActiveElement(),
    translationDiagnostics: getTranslationDiagnostics(),
  };
};

const getRollbarItemMessage = item =>
  item?.body?.trace?.exception?.message ||
  item?.body?.message?.body ||
  item?.body?.message ||
  item?.message ||
  '';

const getRollbarItemFrames = item => item?.body?.trace?.frames || [];

const mergeRollbarItemExtra = (item, extra) => {
  if (item?.body?.trace) {
    item.body.trace.extra = {
      ...(item.body.trace.extra || {}),
      ...extra,
    };
    return;
  }

  item.custom = {
    ...(item.custom || {}),
    ...extra,
  };
};

const isUnresolvableFrame = frame =>
  !frame?.filename ||
  frame.filename === UNKNOWN_FRAME_FILENAME ||
  frame.filename === WEBKIT_MASKED_FRAME_FILENAME;

const hasOnlyUnknownFrames = item => {
  const frames = getRollbarItemFrames(item);
  return frames.length > 0 && frames.every(isUnresolvableFrame);
};

const shouldAttachClientDiagnostics = (isUncaught, item) =>
  isUncaught || STACK_OVERFLOW_MESSAGE_RE.test(getRollbarItemMessage(item));

export const augmentRollbarItem = (isUncaught, args, item) => {
  const { page } = getBrowserContext();

  if (page) {
    item.context = page;
  }

  if (currentUserId) {
    item.person = {
      ...(item.person || {}),
      id: currentUserId,
    };
  }

  if (!shouldAttachClientDiagnostics(isUncaught, item)) {
    return item;
  }

  mergeRollbarItemExtra(item, {
    clientDiagnostics: sanitizeValue(
      item?.body?.trace?.extra?.clientDiagnostics || getClientMonitoringDiagnostics()
    ),
    rollbarDiagnostics: {
      isUncaught,
      originalArgTypes: Array.isArray(args)
        ? args.map(arg => (arg instanceof Error ? 'error' : typeof arg))
        : [],
    },
  });

  return item;
};

const trimRecentBrowserIssueFingerprints = () => {
  const cutoff = Date.now() - RECENT_BROWSER_ISSUE_WINDOW_MS;
  recentBrowserIssueFingerprints = new Map(
    Array.from(recentBrowserIssueFingerprints.entries()).filter(([, timestampMs]) => {
      return timestampMs >= cutoff;
    })
  );
};

const reportKnownBrowserTranslationStackOverflow = errorLike => {
  const diagnostics = getClientMonitoringDiagnostics();
  if (!diagnostics.translationDiagnostics?.likelyActive) {
    return;
  }

  const fingerprint = [
    diagnostics.page || diagnostics.route || 'unknown',
    diagnostics.translationDiagnostics.signals.join(','),
    diagnostics.translationDiagnostics.recentTranslateCspViolations.length,
  ].join('|');

  trimRecentBrowserIssueFingerprints();
  if (recentBrowserIssueFingerprints.has(fingerprint)) {
    return;
  }

  recentBrowserIssueFingerprints.set(fingerprint, Date.now());

  const reportableError =
    errorLike instanceof Error
      ? errorLike
      : new RangeError(
          truncateString(
            errorLike?.message ||
              (typeof errorLike === 'string' ? errorLike : 'Maximum call stack size exceeded.')
          )
        );

  warn(reportableError, 'browser-translation-stack-overflow', {
    browserIssueCategory: 'page-translation',
    isKnownBrowserTranslationIssue: true,
    rollbarIgnoredUncaughtDuplicate: true,
    clientDiagnostics: diagnostics,
  });
};

const handleWindowError = event => {
  const errorLike = event?.error || new RangeError(event?.message || 'Unknown browser error');
  const message = errorLike?.message || event?.message || '';

  if (!STACK_OVERFLOW_MESSAGE_RE.test(message)) {
    return;
  }

  reportKnownBrowserTranslationStackOverflow(errorLike);
};

const setupBrowserDiagnostics = () => {
  if (browserDiagnosticsSetupAttempted || typeof window === 'undefined') {
    return;
  }

  browserDiagnosticsSetupAttempted = true;
  window.addEventListener('securitypolicyviolation', recordSecurityPolicyViolation);
  window.addEventListener('error', handleWindowError, true);
};

export const isIgnoredTranslationTelemetryEvent = event => {
  const message = event?.body?.message;
  return typeof message === 'string'
    ? /Security Policy Violation/i.test(message) && TRANSLATION_RESOURCE_RE.test(message)
    : false;
};

const filterRollbarTelemetry = event => !isIgnoredTranslationTelemetryEvent(event);

export const shouldIgnoreRollbarItem = (isUncaught, _args, item) => {
  const message = getRollbarItemMessage(item);

  // 1. Ignore if message matches any key in ignoreErrorsMap (partial match)
  const isIgnored = Object.keys(ignoreErrorsMap).some(ignoredMsg => message.includes(ignoredMsg));
  if (isIgnored) {
    return true;
  }

  if (!isUncaught) {
    return false;
  }

  if (!STACK_OVERFLOW_MESSAGE_RE.test(message) || !hasOnlyUnknownFrames(item)) {
    return false;
  }

  const clientDiagnostics =
    item?.body?.trace?.extra?.clientDiagnostics || item?.custom?.clientDiagnostics;

  return clientDiagnostics?.translationDiagnostics?.likelyActive === true;
};

const getRollbarPayload = () => {
  const { page } = getBrowserContext();

  return {
    environment: appSettings.rollbarEnv || appSettings.env || process.env.NODE_ENV,
    ...(page ? { context: page } : {}),
    ...(currentUserId ? { person: { id: currentUserId } } : {}),
    client: {
      javascript: {
        source_map_enabled: false,
        guess_uncaught_frames: true,
      },
    },
  };
};

const configureRollbarContext = () => {
  if (rollbar) {
    rollbar.configure({
      payload: getRollbarPayload(),
    });
  }
};

const getRollbarConfig = () => ({
  accessToken: appSettings.rollbarAccessToken,
  enabled: ROLLBAR_ENABLED,
  captureIp: 'anonymize',
  captureUncaught: true,
  captureUnhandledRejections: true,
  captureEmail: false,
  captureUsername: false,
  ignoredMessages,
  scrubFields: rollbarScrubFields,
  scrubTelemetryInputs: true,
  autoInstrument: {
    connectivity: true,
    contentSecurityPolicy: true,
    dom: true,
    errorOnContentSecurityPolicy: false,
    log: false,
    navigation: true,
    network: false,
  },
  filterTelemetry: filterRollbarTelemetry,
  onSendCallback: augmentRollbarItem,
  checkIgnore: shouldIgnoreRollbarItem,
  payload: getRollbarPayload(),
  replay: {
    enabled: true, // activates the session replay feature
    autoStart: true, // starts recording immediately on SDK initialization
    triggers: ['error', 'critical'], // only persist a replay when one of these log levels fires — saves monthly quota
    samplingRatio: 1.0, // record 100% of triggered sessions; lower this if the free quota (1000/month) is exceeded
    preDuration: 60, // seconds of user activity to capture before the triggering event
    postDuration: 5, // seconds of activity to capture after the triggering event
    maskInputOptions: {
      password: true, // mask password field values in the recording
      email: true, // mask email field values in the recording
      tel: true, // mask phone number field values in the recording
    },
  },
});

const normalizeError = errorLike => {
  if (errorLike instanceof Error) {
    return errorLike;
  }

  const message =
    typeof errorLike === 'string'
      ? errorLike
      : errorLike?.message || errorLike?.statusText || 'Unexpected non-error exception reported';
  const error = new Error(message);

  if (errorLike && typeof errorLike === 'object') {
    if (errorLike.name) {
      error.name = errorLike.name;
    }
    if (errorLike.stack) {
      error.stack = errorLike.stack;
    }
    if (errorLike.status != null) {
      error.status = errorLike.status;
    }
    if (errorLike.statusText) {
      error.statusText = errorLike.statusText;
    }
    if (errorLike.code) {
      error.code = errorLike.code;
    }
  }

  return error;
};

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed.
 */
export const setup = () => {
  setupBrowserDiagnostics();

  if (appSettings.sentryDsn) {
    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: appSettings.sentryDsn,
      environment: appSettings.env,
      ignoreErrors: ignoredMessages,
    });
  }

  if (!rollbarSetupAttempted && ROLLBAR_ENABLED) {
    rollbarSetupAttempted = true;

    try {
      rollbar = new Rollbar(getRollbarConfig());
    } catch (error) {
      rollbar = null;
      console.error('Failed to initialize Rollbar', error);
    }
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  currentUserId = userId || null;
  Sentry.setUser(currentUserId ? { id: currentUserId } : null);
  configureRollbarContext();
};

/**
 * Clears the user ID.
 */

export const clearUserId = () => {
  setUserId(null);
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(apiErrors.map(err => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

const responseAPIErrors = error => {
  const errors = error && error.data && error.data.errors ? error.data.errors : [];
  return Array.isArray(errors) ? errors : [];
};

const responseApiErrorInfo = err => {
  const apiErrors = responseAPIErrors(err);
  return apiErrors.map(e => ({
    status: e.status,
    code: e.code,
    meta: sanitizeValue(e.meta),
  }));
};

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const error = (e, code, data) => {
  const reportableError = normalizeError(e);
  const message = reportableError?.message || '';

  // 1. Ignore if message matches any key in ignoreErrorsMap (partial match)
  const isIgnored = Object.keys(ignoreErrorsMap).some(ignoredMsg => message.includes(ignoredMsg));
  if (isIgnored) {
    return;
  }

  const NON_CRITICAL_STATUSES = new Set([
    StatusCode.PAYMENT_REQUIRED,
    StatusCode.FORBIDDEN,
    StatusCode.NOT_FOUND,
    StatusCode.CONFLICT,
    StatusCode.SERVICE_UNAVAILABLE,
    502,
    504,
  ]);
  const status = e?.status || e?.statusCode || e?.response?.status || null;
  const apiErrorsReport = responseApiErrorInfo(e);
  const isNonCritical =
    isNetworkError(e) ||
    NON_CRITICAL_STATUSES.has(Number(status)) ||
    apiErrorsReport.some(err => NON_CRITICAL_STATUSES.has(Number(err.status)));

  if (isNonCritical) {
    warn(e, code, { ...data, isAutoDowngraded: true });
    return;
  }

  const extra = sanitizeValue({
    ...data,
    ...getBrowserContext(),
    ...(currentUserId ? { userId: currentUserId } : {}),
    apiErrorData: apiErrorsReport,
  });

  if (appSettings.sentryDsn) {
    Sentry.withScope(scope => {
      scope.setTag('code', code);
      if (extra?.route) {
        scope.setTag('route', extra.route);
      }
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(reportableError);
    });
  }

  if (rollbar) {
    configureRollbarContext();
    rollbar.error(reportableError, { code, ...extra });
  }

  if (!appSettings.sentryDsn && !rollbar) {
    console.error(reportableError);
    console.error('Error code:', code, 'data:', extra);
    printAPIErrorsAsConsoleTable(apiErrorsReport);
  }

  printAPIErrorsAsConsoleTable(apiErrorsReport);
};

/**
 * Logs a warning with the same metadata guards used by error reporting.
 *
 * @param {Error|String|Object} e Warning payload
 * @param {String} code Warning code
 * @param {Object} data Additional data to be sent to monitoring tools
 */
export const warn = (e, code, data) => {
  const reportableWarning = normalizeError(e);
  const message = reportableWarning?.message || '';

  // 1. Ignore if message matches any key in ignoreErrorsMap (partial match)
  const isIgnored = Object.keys(ignoreErrorsMap).some(ignoredMsg => message.includes(ignoredMsg));
  if (isIgnored) {
    return;
  }

  const apiErrors = responseApiErrorInfo(e);
  const extra = sanitizeValue({
    ...data,
    ...getBrowserContext(),
    ...(currentUserId ? { userId: currentUserId } : {}),
    apiErrorData: apiErrors,
  });

  if (appSettings.sentryDsn) {
    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setTag('code', code);
      if (extra?.route) {
        scope.setTag('route', extra.route);
      }
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(reportableWarning);
    });
  }

  if (rollbar) {
    configureRollbarContext();
    rollbar.warning(reportableWarning, { code, ...extra });
  }

  if (!appSettings.sentryDsn && !rollbar) {
    console.warn(reportableWarning);
    console.warn('Warning code:', code, 'data:', extra);
    printAPIErrorsAsConsoleTable(apiErrors);
  }

  printAPIErrorsAsConsoleTable(apiErrors);
};

const setCause = (error, cause) => {
  const seenErrors = new WeakSet();

  const setCauseIfNoExistingCause = (error, cause) => {
    if (seenErrors.has(error)) {
      return;
    }
    if (error.cause) {
      seenErrors.add(error);
      return setCauseIfNoExistingCause(error.cause, cause);
    }
    error.cause = cause;
  };

  setCauseIfNoExistingCause(error, cause);
};

export const onRecoverableError = (e, componentStack) => {
  let data = {};

  if (componentStack) {
    // Generating this synthetic error allows monitoring services to apply sourcemaps
    // to unminify the stacktrace and make it readable.
    const errorBoundaryError = new Error(e.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = componentStack;

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
    setCause(e, errorBoundaryError);

    data.componentStack = componentStack;
  }

  error(e, 'recoverable-error', data);
};
